import { lazy, Suspense, useState } from 'react'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import {
  NetworkVisibility,
  Space,
  SpaceMembershipStatus,
  SpaceType,
} from '@tribeplatform/gql-client/types'
import { useRouterPath } from '@tribeplatform/react-sdk'
import {
  useAuthMember,
  useAuthToken,
  useLeaveSpace,
} from '@tribeplatform/react-sdk/hooks'
import { type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'

import { useI18n } from '../../i18n/providers/I18nProvider.js'

const SpaceAnalyticsModal = lazy(() =>
  import('../../Analytics/SpaceAnalytics/SpaceAnalyticsModal.js').then(m => ({
    default: m.SpaceAnalyticsModal,
  })),
)
const AuditLogsModal = lazy(() =>
  import('../../AuditLog/AuditLogsModal.js').then(m => ({
    default: m.AuditLogsModal,
  })),
)
const SpaceMembersModal = lazy(() =>
  import('../../MemberList/SpaceMembersModal.js').then(m => ({
    default: m.SpaceMembersModal,
  })),
)
const ShareModal = lazy(() =>
  import('../../ShareModal/ShareModal.js').then(m => ({
    default: m.ShareModal,
  })),
)

export type SpaceActionItem = {
  name: string
  url?: string
  icon?: SvgIconName
  action?: (props?: unknown) => void
  category?: string
  hotkey?: string
}

export const useSpaceActionItems = ({ space }: { space: Space }) => {
  const { $t } = useI18n()
  const { isGuest } = useAuthMember()

  const [auditLogsModal, setAuditLogsModal] = useState<boolean>(false)
  const [spaceAnalyticsModal, setSpaceAnalyticsModal] = useState<boolean>(false)
  const [shareModal, setShareModal] = useState<boolean>(false)
  const [spaceMembersModal, setSpaceMemberModal] = useState<boolean>(false)

  const actionItems: SpaceActionItem[] = []

  const { mutate: leave } = useLeaveSpace()

  const joined =
    space?.authMemberProps?.membershipStatus === SpaceMembershipStatus.joined

  const {
    data: { network },
  } = useAuthToken()

  const [canUpdateNetwork] = hasScopesPermission(network, [
    'updateNetwork',
    'createTemplate',
  ])
  const [canUpdateSpace, canAddSpaceMembers] = hasScopesPermission(space, [
    'updateSpace',
    'addSpaceMembers',
  ])

  const itemCategoryText = $t({
    id: 'Generics.Space',
    defaultMessage: '{SPACE_CC}',
  })

  const pathname = useRouterPath()
  const spacePath = space?.relativeUrl
  const isSpacePage = pathname === spacePath

  if (!space?.id) {
    return { actions: [], children: null }
  }

  actionItems.push({
    action: () => setShareModal(true),
    icon: 'share',
    name: $t({
      id: 'Space.UseSpaceActionItems.Items.ShareSpace',
      defaultMessage: 'Share {SPACE}',
    }),
    category: itemCategoryText,
    hotkey: 'option+s,alt+s',
  })

  if (!isGuest) {
    if (!isSpacePage) {
      actionItems.push({
        url: spacePath,
        icon: 'grid',
        name: $t({
          id: 'Space.Actions.View',
          defaultMessage: 'View {SPACE}',
        }),
        category: itemCategoryText,
      })
    }

    if (canAddSpaceMembers) {
      actionItems.push({
        action: () => setSpaceMemberModal(true),
        icon: 'members',
        name: 'View members',
        category: 'Space',
      })
    }

    if (canUpdateSpace) {
      actionItems.push({
        url:
          space?.type === SpaceType.Page
            ? `/settings/moderation`
            : `/settings/moderation/${space.id}/pending-posts`,
        icon: 'moderation',
        name: $t({
          id: 'Generics.Moderation',
          defaultMessage: 'Moderation',
        }),
        category: itemCategoryText,
        hotkey: 'm',
      })
    }

    if (canUpdateSpace) {
      actionItems.push({
        action: () => setSpaceAnalyticsModal(true),
        icon: 'analytics',
        name: $t({
          id: 'Space.UseSpaceActionItems.Items.SpaceAnalytics',
          defaultMessage: '{SPACE_CC} analytics',
        }),
        hotkey: 'option+a,alt+a',
        category: itemCategoryText,
      })
    }

    if (canUpdateSpace && canUpdateNetwork) {
      actionItems.push({
        action: () => setAuditLogsModal(true),
        icon: 'audit-log',
        name: $t({
          id: 'Generics.AuditLogs',
          defaultMessage: 'Audit logs',
        }),
        hotkey: 'option+l,alt+l',
        category: itemCategoryText,
      })
    }

    if (canUpdateNetwork) {
      actionItems.push({
        url: `/manage/customizer/spaces/${space.id}/blocks`,
        icon: 'customizer',
        name: $t({
          id: 'Generics.Customize',
          defaultMessage: 'Customize',
        }),
        category: itemCategoryText,
        hotkey: 'c',
      })
    }

    if (joined) {
      actionItems.push({
        action: () => leave({ spaceId: space?.id }),
        icon: 'logout',
        name: $t({
          id: 'Space.UseSpaceActionItems.Items.LeaveSpace',
          defaultMessage: 'Leave {SPACE}',
        }),
        category: itemCategoryText,
        hotkey: 'j',
      })
    }
  }

  const children = (
    <>
      {shareModal && (
        <Suspense fallback={<></>}>
          <ShareModal
            isPrivate={
              space?.hidden || network.visibility === NetworkVisibility.private
            }
            spaceId={space?.id}
            open
            onClose={() => setShareModal(false)}
            title={space.name}
            url={space?.url}
          />
        </Suspense>
      )}
      {auditLogsModal && (
        <Suspense fallback={<></>}>
          <AuditLogsModal
            open
            onClose={() => setAuditLogsModal(false)}
            spaceId={space?.id}
          />
        </Suspense>
      )}
      {spaceAnalyticsModal && (
        <Suspense fallback={<></>}>
          <SpaceAnalyticsModal
            open
            onClose={() => setSpaceAnalyticsModal(false)}
            space={space}
            linkToContent={false}
          />
        </Suspense>
      )}
      {spaceMembersModal && (
        <Suspense fallback={<></>}>
          <SpaceMembersModal
            open
            onClose={() => setSpaceMemberModal(false)}
            space={space}
          />
        </Suspense>
      )}
    </>
  )

  return { actions: actionItems, children }
}
